import { Unsaved } from "./misc";
import {
  GenericReferenceId,
  Maybe,
  Objective,
  ObjectivesItem,
  Resource,
  Tag,
} from "./schema/generated";
import { ParsedUnit } from "./unit";
import { Replace } from "./util";

export type TeksCategory =
  | "PROCESS"
  | "READINESS"
  | "SUPPORTING"
  | "RTC"
  | "SEP";

export type TeksCurriculumUsage = "DIRECTLY_TAUGHT" | "DEVELOPING" | "ONGOING";

type TeksCategoryTag = {
  key: "tcmpc-teks-objective-category";
  type: "string";
  value: TeksCategory;
};

type TeksCurriculumUsageTag = {
  key: "tcmpc-teks-curriculum-usage";
  type: "string";
  value: TeksCurriculumUsage;
};

export type TeksObjectivesItem = Replace<
  ObjectivesItem,
  {
    tags?: [] | [TeksCurriculumUsageTag] | null;
    objective: Replace<
      Objective,
      {
        tags?: Array<Tag | TeksCategoryTag> | null;
      }
    >;
  }
>;

export type TeksObjectivesReference = Replace<
  GenericReferenceId,
  {
    tags?: [] | [TeksCurriculumUsageTag] | null;
  }
>;

export interface TCMPCUnitData {
  type: "tcmpc";
  introduction: string;
  priorToUnit: string;
  duringUnit: string;
  afterUnit: string;
  additionalNotes: string;
  inquiry: string;
  research: string;
  suggestedTools?: string;
  vocabulary?: string;
  tcrContent?: string;
  spiralingSuggestions?: string;
  misconceptions: {
    id: string;
    text: string;
  }[];
  underdevelopedConcepts: {
    id: string;
    text: string;
  }[];
  subUnits: TCMPCBundle[];
}

export interface TCMPCRowData {
  id: string;
  columns: {
    understandings: {
      span: number;
      data: string;
    };
    concepts: {
      span: number;
      data: string;
    };
    assessments: {
      span: number;
      data: {
        id: string;
        sk: string;
        data: Resource | undefined;
        // hydrated data
        // title: string;
        // text: string;
        // alignments: {
        //   id: string;
        //   // other hydrated alignment fields as needed
        //   numRaw: string;
        // }[];
      }[];
    };
  };
}

export interface TCMPCBundle {
  description: string;
  ouaqs: string;
  table: {
    data: TCMPCRowData[];
  };
}

export type ParsedTCMPCUnit = Replace<
  ParsedUnit,
  {
    data: TCMPCUnitData;
    objectives?: Maybe<TeksObjectivesItem[]>;
    objectivesIds?: Maybe<TeksObjectivesReference[]>;
    subUnits?: never;
    type: "tcmpc";
  }
>;

export const TeksCategoryMeta: {
  [key in TeksCategory]: { label: string; abbreviation: string };
} = {
  PROCESS: {
    label: "Process",
    abbreviation: "P",
  },
  READINESS: {
    label: "Readiness",
    abbreviation: "R",
  },
  SUPPORTING: {
    label: "Supporting",
    abbreviation: "S",
  },
  RTC: {
    label: "Recurring Themes and Concepts",
    abbreviation: "RTC",
  },
  SEP: {
    label: "Science and Engineering Practices",
    abbreviation: "SEP",
  },
};

export const TeksCategoryList: TeksCategory[] = [
  "PROCESS",
  "READINESS",
  "SUPPORTING",
  "RTC",
  "SEP",
];

export const TeksCurriculumUsageMeta: {
  [key in TeksCurriculumUsage]: {
    label: string;
    abbreviation: string;
    short: string;
  };
} = {
  DIRECTLY_TAUGHT: {
    label: "Taught Directly",
    short: "Direct",
    abbreviation: "T",
  },
  DEVELOPING: {
    label: "Developing",
    short: "Dev.",
    abbreviation: "D",
  },
  ONGOING: {
    label: "Ongoing",
    short: "Ongoing",
    abbreviation: "O",
  },
};

export const TeksCurriculumUsageList: TeksCurriculumUsage[] = [
  "DIRECTLY_TAUGHT",
  "DEVELOPING",
  "ONGOING",
];

export type ParsedObjectivesItem = Replace<
  ObjectivesItem,
  {
    data?: Partial<{
      content: string;
      notes: string;
    }>;
  }
>;

export type ParsedTeksObjectivesItem = Replace<
  TeksObjectivesItem,
  {
    data?: Partial<{
      content: string;
      notes: string;
    }>;
  }
>;

export type ParsedUnitObjective = Objective & {
  objectivesItem?: Omit<ParsedObjectivesItem, "objective">;
};

export const isTcmpcUnit = (
  unit: Unsaved<ParsedUnit | ParsedTCMPCUnit>,
): unit is ParsedTCMPCUnit => {
  return isTcmpcUnitData(unit.data);
};

export const isTcmpcUnitData = (data: unknown): data is TCMPCUnitData => {
  return (
    typeof data === "object" &&
    data != null &&
    "type" in data &&
    data.type === "tcmpc"
  );
};
